/**
 * LaneV2TaskOutcome — what a change task did, in the center chat, in the turn, above its story strip.
 *
 * Shown from `changeset_proposed` onward, and for any task that holds a package with something on offer (`actions`): a validated
 * task a newer request replaced (`superseded`) is not in the states below but keeps its Save (UX map D5) (never for a revert-shaped task, which has its own card). It reads the R3
 * snapshot live, so it is also the task-end summary: one row per changed component (op glyph, name, validation verdict
 * with its reasons, "View diff" onto that file in the Task section), the package's branch-save status, the last check's
 * verdict (a failed check is labelled, never read as a pass), and the SAME server-driven Save / Validate row the Task
 * section uses (`PostBuildActions`; the buttons are `TaskSnapshot.actions`, this card decides nothing).
 */
import type { SaveBranchRunOpts } from "@/stores/laneV2SessionStore";
import type { SaveBranchSettled } from "@/services/multi-agent/laneV2/branchSave";
import { useId, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronRight, Circle, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ncoder/ui";
import { cn } from "@/lib/utils";
import { EntityChip } from "@/components/molecules/multi-agent/EntityChip";
import { buildChipIndex, chipTargetFor } from "@/services/multi-agent/laneV2/entityChips";
import { isRevertShaped } from "./changeTarget";
import { OP_GLYPH } from "./LaneV2PlanSummary";
import { MemberFailureLine, MemberNotes, MemberStatusMark, memberNoteCount, useOpenNotes, SaveToBranchPanel, classifySaveBranchError, saveFailureLine, saveProgressLabel } from "./PackageView";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import { PostBuildActions } from "./PostBuildActions";
import { rechecksLeft } from "./rechecks";
import { formatClock, formatLocalTimestamp, isHeldBack, isNotWritten, memberOpBlurb, memberOpWord } from "./views";
import { restingCheckFailed, restingVerdictOf } from "@/services/multi-agent/laneV2/presentation";
import { TERMINAL_TASK_STATES, type SaveBranchIn, type TaskSnapshot } from "@/services/multi-agent/laneV2/types";

/** A not-written member's marker: a warning "!" in place of the ± it never earned. */
const NOT_WRITTEN_GLYPH = { glyph: "!", className: "text-[var(--ma-state-warn-text)]" };

/** Task states from which the outcome card shows by state alone; a finished task (a superseded one) shows it while `actions` offers something. A repairing task (building, held, needs_input) keeps its old package but gets no card. */
export const OUTCOME_STATES: ReadonlySet<string> = new Set(["changeset_proposed", "checking", "validated", "applying", "applied"]);

export function showsOutcome(snapshot: TaskSnapshot | null | undefined): snapshot is TaskSnapshot {
  return (
    !!snapshot &&
    !!snapshot.package &&
    !isRevertShaped(snapshot) &&
    (OUTCOME_STATES.has(snapshot.state) || (TERMINAL_TASK_STATES.has(snapshot.state) && (snapshot.actions?.length ?? 0) > 0))
  );
}

/** One line for the latest check: passed / failed (labelled) / no result / not checked. */
export function checkVerdictLine(snapshot: Pick<TaskSnapshot, "state" | "validation"> & Partial<Pick<TaskSnapshot, "rest_verdict">>): { text: string; tone: "ok" | "err" | "neutral" } {
  // At rest in `validated` the typed verdict is the only source; last_check is consulted only through its null fallback.
  const rest = restingVerdictOf(snapshot);
  if (rest) return rest.passed ? { text: "Last check passed", tone: "ok" } : { text: rest.failed ? "Last check failed" : rest.words, tone: rest.failed ? "err" : "neutral" };
  const check = snapshot.validation?.last_check ?? null;
  if (snapshot.state === "checking") return { text: "Checking against the org...", tone: "neutral" };
  if (!check) return { text: "Not checked against the org yet", tone: "neutral" };
  if (check.ok === true) return { text: "Last check passed", tone: "ok" };
  if (check.ok === false) return { text: "Last check failed", tone: "err" };
  return { text: "The org has not reported a result for the last check", tone: "neutral" };
}

/**
 * The card's one status line (UX map 5.5 / tweak 6: the story strip folds into the card header): "N components changed ·
 * Checked 14:02 · $0.41". A failed check at rest leads with "Needs attention" (amber, never read as a pass); a running check
 * is the busy line. Pure, so the copy is pinned without a DOM.
 */
export interface OutcomeHeader {
  tone: "ok" | "err" | "busy" | "neutral";
  /** The visible state word: "Needs attention" (failed check at rest), "Ready" (passed, at rest), "Applied"; else null. */
  lead: string | null;
  /** What the status icon says to a screen reader ("Ready", "Applied", "Needs attention", "Checking"); the visible line stays the one sentence. */
  iconLabel: string | null;
  title: string;
  /** The muted parts after the title: check time, cost. */
  parts: string[];
}

export function outcomeHeader(snapshot: Pick<TaskSnapshot, "state" | "validation" | "package"> & Partial<Pick<TaskSnapshot, "rest_verdict">>, usageLabel: string | null = null): OutcomeHeader {
  // A member the build wrote nothing for is listed, but it did not change: it is not counted as changed (dev 2026-10-31,
  // "3 components changed" over a class whose method was never written).
  const members = snapshot.package?.members ?? [];
  const unwritten = members.filter(isNotWritten).length;
  const n = members.length - unwritten;
  const noun = n === 1 ? "component" : "components";
  const title = unwritten > 0 ? `${n} ${noun} changed · ${unwritten} not changed` : `${n} ${noun} changed`;
  const check = snapshot.validation?.last_check ?? null;
  const at = check?.completed_date ? formatClock(check.completed_date) : null;
  const parts: string[] = [];
  let tone: OutcomeHeader["tone"] = "neutral";
  let lead: string | null = null;
  const rest = restingVerdictOf(snapshot);
  if (snapshot.state === "checking") {
    tone = "busy";
    parts.push("Checking against the org\u2026");
  } else if (rest) {
    // The typed verdict decides; last_check.ok is not read.
    if (rest.passed) {
      tone = "ok";
      parts.push(at ? `Checked ${at}` : "Checked");
    } else if (rest.failed) {
      tone = "err";
      lead = "Needs attention";
      parts.push(at ? `Check failed ${at}` : "Check failed");
    } else {
      lead = rest.label;
      parts.push(rest.tone === "warn" ? "Check did not run" : rest.label === "Nothing to check" ? "Nothing to check" : "Check result not understood");
    }
  } else if (check?.ok === true) {
    tone = "ok";
    parts.push(at ? `Checked ${at}` : "Checked");
  } else if (check?.ok === false) {
    tone = "err";
    lead = restingCheckFailed(snapshot) ? "Needs attention" : null;
    parts.push(at ? `Check failed ${at}` : "Check failed");
  }
  if (usageLabel) parts.push(usageLabel);
  // D6: the resting state is a visible word, like the amber "Needs attention": "Ready" (passed, at rest) or "Applied".
  if (snapshot.state === "applied") lead = "Applied";
  else if (tone === "ok" && snapshot.state === "validated") lead = "Ready";
  const iconLabel = lead ? null : tone === "busy" ? "Checking" : tone === "err" ? "Check failed" : null;
  return { tone, lead, iconLabel, title, parts };
}

/** Shown in place of Save / Validate while the dock asks the reader to approve the changes (UX map C11): one action owner at a time. */
export const APPROVE_FIRST_HINT = "Approve to check against the org";

export interface LaneV2TaskOutcomeProps {
  snapshot: TaskSnapshot;
  /** The changeset approval is pending in the dock: the card offers no competing action, only the hint. */
  awaitingApproval?: boolean;
  /** Open the Task section on one work item's diff (the landing file is picked there). */
  onViewItem?: (taskId: string, workItemId: string) => void;
  onOpenTask?: (taskId: string) => void;
  /** Present only when R0 advertises `branch_save`. */
  onSaveBranch?: (taskId: string, body: SaveBranchIn, opts?: SaveBranchRunOpts) => Promise<SaveBranchSettled>;
  onValidate?: (taskId: string, idempotencyKey: string) => Promise<unknown>;
  onRequestChanges?: (taskId: string, text: string, idempotencyKey: string) => Promise<unknown>;
  onDiscard?: (taskId: string, idempotencyKey: string) => Promise<unknown>;
  /** The cost of all the task's turns ("$0.41"), shown in its status line. */
  usageLabel?: string | null;
  /** R0 `offline`: passed to the actions row. */
  offline?: boolean;
  className?: string;
}

export function LaneV2TaskOutcome({ snapshot, awaitingApproval = false, onViewItem, onOpenTask, onSaveBranch, onValidate, onRequestChanges, onDiscard, usageLabel = null, offline = false, className }: LaneV2TaskOutcomeProps) {
  const [saveOpen, setSaveOpen] = useState(false);
  const trackedSave = useLaneV2SessionStore((st) => st.saves[snapshot.task_id]);
  const notesOpen = useOpenNotes();
  const notesPrefix = useId();
  const pkg = snapshot.package;
  // Names are the same chip as in the prose: this task's package with a diff opens it, anything else the explorer.
  const chips = useMemo(() => buildChipIndex({ [snapshot.task_id]: snapshot }).byKey, [snapshot]);
  if (!pkg) return null;
  const verdict = checkVerdictLine(snapshot);
  const head = outcomeHeader(snapshot, usageLabel);
  const lastSave = pkg.branch_saves?.[0] ?? null;
  const taskId = snapshot.task_id;
  const failedAtRest = restingCheckFailed(snapshot);
  const noteKeys = pkg.members.flatMap((m, i) => (memberNoteCount({ reasons: m.validation_reasons, notWritten: isNotWritten(m) }) > 0 ? [String(i)] : []));
  const allNotesOpen = noteKeys.length > 0 && noteKeys.every((k) => notesOpen.open.has(k));
  return (
    <section
      aria-label="What changed"
      data-lane-v2-outcome={taskId}
      data-lane-v2-outcome-state={snapshot.state}
      className={cn("rounded-md border border-border bg-card", className)}
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3.5 py-2" data-lane-v2-outcome-header={head.tone}>
        <HeaderIcon tone={head.tone} label={head.iconLabel} />
        <h3 className="text-[13px] font-semibold text-foreground">
          {head.lead && <span className={head.tone === "err" || head.lead === "Needs attention" ? "text-[var(--ma-state-warn-text)]" : head.tone === "ok" || head.lead === "Applied" ? "text-[var(--ma-state-ok-text)]" : "text-muted-foreground"}>{head.lead} · </span>}
          {head.title}
        </h3>
        {head.parts.length > 0 && <span className="text-[12px] text-muted-foreground">· {head.parts.join(" · ")}</span>}
        {noteKeys.length > 0 && (
          <button
            type="button"
            onClick={() => notesOpen.toggleAll(noteKeys)}
            data-lane-v2-toggle-all-notes=""
            className="ml-auto text-[12px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {allNotesOpen ? "Hide all notes" : "Show all notes"}
          </button>
        )}
        {onOpenTask && (
          <button
            type="button"
            onClick={() => onOpenTask(taskId)}
            data-lane-v2-open-task={taskId}
            className={cn(noteKeys.length === 0 && "ml-auto", "inline-flex items-center gap-0.5 text-[13px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
          >
            Details
            <ChevronRight aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
      </header>
      <TooltipProvider delayDuration={150}>
      <ul className="border-t border-border" data-lane-v2-outcome-rows="">
        {pkg.members.map((m, i) => {
          const notWritten = isNotWritten(m);
          const noteKey = String(i);
          const panelId = `${notesPrefix}-notes-${i}`;
          const isOpen = notesOpen.open.has(noteKey) && noteKeys.includes(noteKey);
          const op = notWritten ? NOT_WRITTEN_GLYPH : (OP_GLYPH[m.op] ?? { glyph: "·", className: "text-muted-foreground" });
          return (
            <li key={`${m.metadata_type}-${m.full_name}-${i}`} className="border-t border-border/60 px-3.5 py-1.5 first:border-t-0" data-lane-v2-outcome-row={`${m.metadata_type}:${m.full_name}`}>
              <div className="flex items-baseline gap-2 text-[12.5px]">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      role="img"
                      tabIndex={0}
                      aria-label={memberOpWord(m.op, notWritten)}
                      data-member-op={notWritten ? "not_written" : m.op}
                      className={cn("w-3 shrink-0 cursor-default text-center font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", op.className)}
                    >
                      {op.glyph}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" className="max-w-xs px-2.5 py-1.5 text-[12px]">
                    <span className="font-semibold">{memberOpWord(m.op, notWritten)}</span>
                    <span className="block text-muted-foreground">{memberOpBlurb(m.op, notWritten)}</span>
                  </TooltipContent>
                </Tooltip>
                <span className="min-w-0 truncate">
                  <EntityChip target={chipTargetFor(chips, m.metadata_type, m.full_name, m.op !== "create")} onOpenDiff={onViewItem} />
                </span>
                <MemberStatusMark status={m.validation_status} reasons={m.validation_reasons} heldBack={isHeldBack(m)} notWritten={notWritten} open={isOpen} onToggle={() => notesOpen.toggle(noteKey)} panelId={panelId} />
                <span className="ml-auto shrink-0 pl-2 text-[11px] text-muted-foreground">{m.metadata_type}</span>
              </div>
              <div className="ml-5">
                <MemberFailureLine status={m.validation_status} reasons={m.validation_reasons} notWritten={notWritten} />
                {isOpen && <MemberNotes id={panelId} reasons={m.validation_reasons} notWritten={notWritten} className="mt-1" />}
              </div>
            </li>
          );
        })}
      </ul>
      </TooltipProvider>
      <div className="space-y-1.5 border-t border-border px-3.5 py-2.5">
        {verdict.tone !== "ok" && snapshot.state !== "checking" && (
        <p
          className={cn("text-[12px]", verdict.tone === "err" ? "text-ma-err-text" : "text-muted-foreground")}
          data-lane-v2-outcome-check={failedAtRest ? "failed" : verdict.tone}
        >
          {verdict.text}
        </p>
        )}
        {!lastSaveInFlight(lastSave, pkg?.branch_save_running) && (
        <p className="text-[12px] text-muted-foreground" data-lane-v2-outcome-save={lastSave ? "saved" : "unsaved"}>
          {lastSave ? (
            <>
              Saved v{lastSave.version} to <span className="font-mono text-foreground">{lastSave.branch}</span> ({lastSave.files_count} {lastSave.files_count === 1 ? "file" : "files"}, {formatLocalTimestamp(lastSave.at)})
            </>
          ) : (
            "Not saved to a branch yet"
          )}
        </p>
        )}
        {!saveOpen && <SaveStateLine running={pkg?.branch_save_running ?? null} tracked={trackedSave} />}
        {awaitingApproval ? (
          <p className="text-[13px] font-medium text-foreground" data-lane-v2-outcome-approve-hint="">
            {APPROVE_FIRST_HINT}
          </p>
        ) : (
          <PostBuildActions
            actions={snapshot.actions ?? (onSaveBranch ? ["save"] : [])}
            taskState={snapshot.state}
            onSave={onSaveBranch ? () => setSaveOpen((v) => !v) : undefined}
            saveOpen={saveOpen}
            rechecksLeft={rechecksLeft(snapshot.transitions)}
            offline={offline}
            saveRunning={pkg?.branch_save_running != null}
            onValidate={onValidate ? (key) => onValidate(taskId, key) : undefined}
            onRequestChanges={onRequestChanges ? (text, key) => onRequestChanges(taskId, text, key) : undefined}
            onDiscard={onDiscard ? (key) => onDiscard(taskId, key) : undefined}
          />
        )}
      </div>
      {onSaveBranch && saveOpen && !awaitingApproval && (
        <SaveToBranchPanel
          key={pkg.package_hash}
          packageHash={pkg.package_hash}
          members={pkg.members}
          taskId={taskId}
          running={pkg.branch_save_running ?? null}
          onSaveBranch={forwardSaveBranch(onSaveBranch, taskId)}
        />
      )}
    </section>
  );
}

function HeaderIcon({ tone, label }: { tone: OutcomeHeader["tone"]; label: string | null }) {
  // The card's one accent: the status icon (the primary button is the other).
  const icon =
    tone === "ok" ? (
      <CheckCircle2 aria-hidden className="h-4 w-4 shrink-0 text-[var(--ma-state-ok-text)]" />
    ) : tone === "err" ? (
      <AlertTriangle aria-hidden className="h-4 w-4 shrink-0 text-[var(--ma-state-warn-text)]" />
    ) : tone === "busy" ? (
      <Loader2 aria-hidden className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
    ) : (
      <Circle aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={2} />
    );
  return (
    <>
      {icon}
      {label && <span className="sr-only">{label}</span>}
    </>
  );
}

/** Binds a task to the card's save callback and forwards the abort signal + progress callback the panel passes, so a
 *  background save's polling is abortable and its "N chunks written" line reaches the panel. */
export function forwardSaveBranch(
  onSaveBranch: (taskId: string, body: SaveBranchIn, opts?: SaveBranchRunOpts) => Promise<SaveBranchSettled>,
  taskId: string,
): (body: SaveBranchIn, opts?: SaveBranchRunOpts) => Promise<SaveBranchSettled> {
  return (body, opts) => onSaveBranch(taskId, body, opts);
}

/** The save's state outside the panel: running (R3's word) or the last unattended failure. Details live in the Save panel. */
export function SaveStateLine({ running, tracked }: { running: NonNullable<TaskSnapshot["package"]>["branch_save_running"]; tracked: ReturnType<typeof useLaneV2SessionStore.getState>["saves"][string] | undefined }) {
  if (running) {
    const chunks = tracked?.phase === "running" ? tracked.chunks : running.chunks_written;
    return (
      <p role="status" data-lane-v2-outcome-save-state="running" className="text-[12px] text-muted-foreground">
        {running.mode === "async" ? saveProgressLabel(chunks) : "A save is running; it finishes shortly."}
      </p>
    );
  }
  if (tracked?.phase === "failed") {
    return (
      <p data-lane-v2-outcome-save-state="failed" className="text-[12px] text-ma-warn-text">
        {saveFailureLine(classifySaveBranchError(tracked.error))} Open Save for details.
      </p>
    );
  }
  return null;
}

/**
 * While a save runs, R3 `branch_saves` rows (grouped per save, `files_count` growing as chunks land) at or after the
 * running save's start are that save's partial progress, not a finished save: the card must not show them as final.
 */
export function lastSaveInFlight(
  lastSave: { at: string } | null | undefined,
  running: { started_at: string } | null | undefined,
): boolean {
  if (!lastSave || !running) return false;
  const at = Date.parse(lastSave.at);
  const started = Date.parse(running.started_at);
  return Number.isFinite(at) && Number.isFinite(started) && at >= started;
}
